import { useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Avatar, Badge, Brand, Button, Dialog, Icon } from '../components/index.js';
import type { IconName } from '../components/index.js';
import { useCurrentUser, useLogout } from '../lib/auth.js';
import { useUnreadNotificationCount } from '../lib/notifications.js';
import { ACCOUNT_NAV_ITEMS, NAV_ICON_CLASS, primaryNavItemsFor } from './nav-items.js';
import type { NavItem } from './nav-items.js';

function navLinkClassName(isDark: boolean, showLabel: boolean) {
  return ({ isActive }: { isActive: boolean }): string => {
    // Icon-only links are square so every glyph keeps at least a 44px
    // interactive target (docs/UI_GUIDELINES.md §9) regardless of how wide
    // its artwork is; labelled drawer rows keep the horizontal padding.
    const base = showLabel
      ? 'relative flex min-h-touch items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold'
      : 'relative flex min-h-touch min-w-touch items-center justify-center rounded-md border p-2';
    const focus =
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-frame';
    if (isActive) {
      return `${base} ${focus} border-accent bg-brand text-textOnBrand shadow-1`;
    }
    return isDark
      ? `${base} ${focus} border-transparent text-textOnDarkMuted hover:border-brand/50 hover:bg-frameElevated hover:text-textOnDark`
      : `${base} ${focus} border-transparent text-textSecondary hover:border-border hover:bg-surfaceAlt hover:text-textPrimary`;
  };
}

function UnreadBadge({ count }: { count: number }) {
  return <Badge variant="danger">{count > 99 ? '99+' : count}</Badge>;
}

function NavIconLink({
  to,
  label,
  icon,
  showLabel,
  isDark,
  onNavigate,
  badgeCount = 0,
  end = false,
}: {
  to: string;
  label: string;
  icon: IconName;
  showLabel: boolean;
  isDark: boolean;
  onNavigate?: () => void;
  badgeCount?: number;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={navLinkClassName(isDark, showLabel)}
      onClick={onNavigate}
      end={end}
      aria-label={label}
      // A hover/focus tooltip for the icon-only desktop bar. The accessible
      // name comes from `aria-label` plus the visually-hidden text below —
      // never from this attribute alone (docs/UI_GUIDELINES.md §9).
      title={label}
    >
      <span className="relative inline-flex">
        <Icon name={icon} className={showLabel ? NAV_ICON_CLASS.mobile : NAV_ICON_CLASS.desktop} />
        {badgeCount > 0 && !showLabel && (
          <span className="absolute -right-2 -top-2">
            <UnreadBadge count={badgeCount} />
          </span>
        )}
      </span>
      {showLabel ? (
        <span className="inline-flex items-center gap-1.5">
          {label}
          {badgeCount > 0 && <UnreadBadge count={badgeCount} />}
        </span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </NavLink>
  );
}

/** The unread badge belongs to exactly one destination; keyed off the shared config, not a route string. */
function badgeFor(item: NavItem, unreadCount: number): number {
  return item.key === 'notifications' ? unreadCount : 0;
}

export function Header() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const unread = useUnreadNotificationCount(!!user);
  const unreadCount = unread.data ?? 0;
  const isAdmin = user?.role === 'admin';
  // Signed out, there is no application navigation to show — only the
  // sign-in controls (`nav-items.ts`'s `primaryNavItemsFor`). Both layouts
  // read the same value, so neither can render a nav region the other hides.
  const primaryItems = primaryNavItemsFor({ isSignedIn: !!user, isAdmin });
  const drawerItems = [...primaryItems, ...(user ? ACCOUNT_NAV_ITEMS : [])];

  return (
    <header className="app-header-surface sticky top-0 z-30 border-b-strong border-brand/70 bg-frame shadow-3">
      {/* Mobile shows only the brand mark and the menu button (the nav rows
          below only appear at `wide:`), so a shorter bar keeps just as much
          touch-target room while leaving more viewport for page content —
          most noticeable in a full-bleed game like rogue-rush, whose scene
          starts right below this header. The tablet+ height is `min-h-header`
          (`layout.headerHeight`), not a hand-typed `4.5rem`: rogue-rush's
          full-bleed scene positions its own HUD from that same token in its
          own `theme.css` — the two independently drifted to 4.5rem/4.625rem
          before, and nothing but a matching literal in each file can keep
          them in sync, since the values can't share a single CSS variable
          across a React className and a plain stylesheet. */}
      <div className="mx-auto flex min-h-[3.5rem] max-w-shell items-center gap-4 px-4 py-2 tablet:min-h-header tablet:px-6">
        <div className="shrink-0">
          <Brand />
        </div>

        {/*
          Primary navigation owns all the horizontal space between the brand
          and the account controls (`flex-1`) and distributes its items
          evenly across it (`justify-evenly`), so spacing grows with the
          viewport instead of being pinned by fixed margins. The account
          group stays its own shrink-0 cluster on the right, so `Log out`
          never drifts away from the username it belongs to.
        */}
        {primaryItems.length > 0 && (
          <nav
            aria-label="Primary"
            className="hidden min-w-0 flex-1 items-center justify-evenly gap-1 wide:flex"
          >
            {primaryItems.map((item) => (
              <NavIconLink
                key={item.key}
                to={item.to}
                label={item.label}
                icon={item.icon}
                showLabel={false}
                isDark
                end={item.end}
                badgeCount={badgeFor(item, unreadCount)}
              />
            ))}
          </nav>
        )}

        {/* `ml-auto` keeps the account cluster hard right even when there is
            no primary navigation to push it there (signed out). */}
        <div className="ml-auto hidden shrink-0 items-center gap-2 border-l border-brand/25 pl-3 wide:flex">
          {user ? (
            <>
              <NavLink
                to={`/users/${encodeURIComponent(user.username)}`}
                // Underlined rather than filled when active: this control is
                // the player's own avatar and name, and the gold pill the
                // icon links use would bury the portrait it exists to show.
                className={({ isActive }) =>
                  `flex min-h-touch items-center gap-2 rounded-md border-b-2 px-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-frame ${
                    isActive
                      ? 'border-brand text-textOnDark'
                      : 'border-transparent text-textOnDarkMuted hover:text-textOnDark'
                  }`
                }
                // The username text is the accessible name; the tooltip says
                // where the control goes. No `aria-label` — overriding the
                // name here would rename the one control users identify by
                // their own username.
                title="Profile"
              >
                <Avatar
                  seed={user.id}
                  label={user.username}
                  config={user.avatar}
                  size="sm"
                  loading="eager"
                />
                {user.username}
              </NavLink>
              <NavIconLink
                to="/settings"
                label="Settings"
                icon="settings"
                showLabel={false}
                isDark
              />
              <button
                type="button"
                className="flex min-h-touch min-w-touch items-center justify-center rounded-md border border-transparent p-2 text-textOnDarkMuted hover:bg-frameElevated hover:text-textOnDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-frame disabled:opacity-60"
                aria-label="Log out"
                title="Log out"
                aria-busy={logout.isPending || undefined}
                disabled={logout.isPending}
                onClick={() => logout.mutate()}
              >
                <Icon name="logout" className={NAV_ICON_CLASS.desktop} />
                <span className="sr-only">Log out</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="rounded-md px-2 py-2 text-sm font-semibold text-textOnDarkMuted hover:text-textOnDark"
              >
                Log in
              </Link>
              <Link to="/register">
                <Button size="sm">Register</Button>
              </Link>
            </>
          )}
        </div>

        <Button
          ref={mobileMenuButtonRef}
          variant="ghost"
          size="sm"
          className="!ml-auto !min-w-touch !px-2 !text-textOnDark hover:!bg-frameElevated wide:hidden"
          aria-label="Open navigation menu"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen(true)}
        >
          <Icon name="menu" className={NAV_ICON_CLASS.mobile} />
        </Button>
      </div>

      <Dialog
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        onCloseAutoFocus={() => mobileMenuButtonRef.current?.focus()}
        title="Navigation"
      >
        {/*
          The drawer renders the same configuration as the bar above, with
          labels: primary destinations, then the account destinations
          (including Profile, which the desktop header folds into the avatar
          control instead). Signed out, both lists are empty and the drawer
          holds only the sign-in controls below.
        */}
        {drawerItems.length > 0 && (
          <nav aria-label="Primary" className="flex flex-col gap-1">
            {drawerItems.map((item) => (
              <NavIconLink
                key={item.key}
                to={item.to}
                label={item.label}
                icon={item.icon}
                showLabel
                isDark={false}
                end={item.end}
                badgeCount={badgeFor(item, unreadCount)}
                onNavigate={() => setMobileNavOpen(false)}
              />
            ))}
          </nav>
        )}
        {/*
          Account/session information, separated from the destinations above
          by a rule: "signed in as …" is an identity statement, and `Log
          out` ends a session rather than going somewhere, so neither should
          read as another row in the navigation list. With no destinations
          above it (signed out), there is nothing to separate it from, so the
          rule and its spacing go too.
        */}
        <div
          className={
            drawerItems.length > 0
              ? 'mt-5 flex flex-col gap-3 border-t border-divider pt-5'
              : 'flex flex-col gap-3'
          }
        >
          {user ? (
            <>
              <Link
                to={`/users/${encodeURIComponent(user.username)}`}
                className="flex items-center gap-2 rounded-md text-sm text-textSecondary hover:text-textPrimary"
                onClick={() => setMobileNavOpen(false)}
              >
                <Avatar
                  seed={user.id}
                  label={user.username}
                  config={user.avatar}
                  size="sm"
                  loading="eager"
                />
                Signed in as {user.username}
              </Link>
              <Button
                variant="secondary"
                isLoading={logout.isPending}
                onClick={() => {
                  logout.mutate();
                  setMobileNavOpen(false);
                }}
              >
                Log out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileNavOpen(false)}>
                <Button variant="secondary" className="w-full">
                  Log in
                </Button>
              </Link>
              <Link to="/register" onClick={() => setMobileNavOpen(false)}>
                <Button className="w-full">Register</Button>
              </Link>
            </>
          )}
        </div>
      </Dialog>
    </header>
  );
}
